import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDropDown,
  FactCheck,
  MoreVert,
  People,
  Preview,
  Search,
  Sync,
  CreditCard,
  PauseCircleOutline,
  PlayCircleOutline,
} from '@mui/icons-material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useImpersonation } from '../contexts/ImpersonationContext';
import {
  DataTableShell,
  EmptyState,
  InlineMessage,
  PageContainer,
  PageHeader,
  StatusChip,
  TableSkeleton,
  statusLabel,
} from '../components/ui';
import { tokens } from '../theme/tokens';
import { useIsMobile } from '../hooks/useIsMobile';
import { bvRoutes } from './brandsVendors/brandVendorsRoutes';
import { REVIEWABLE_STORE_STATUSES, storeCaseId } from '../services/storeApprovalApi';
import {
  ALL_STATUSES,
  DEFAULT_STATUSES,
  PLAN_FILTERS,
  SORTS,
  SUBSCRIPTION_FILTERS,
  SUBSCRIPTION_LABELS,
  StoreAccountPage,
  StoreAccountRow,
  daysLeft,
  grantKindLabel,
  isDefaultStatuses,
  isStaleActivity,
  longDate,
  planLabel,
  planTone,
  relativeTime,
  seatsTone,
  statusFilterLabel,
  storeStatusTone,
  subscriptionNote,
  subscriptionTone,
} from './adminStores/storeAccounts';
import { SuspendStoreDialog, useStoreStatusMutations } from './adminStores/StoreStatusActions';

export const storeDetailPath = (orgId: string, tab?: string) =>
  `/app/admin/stores/${encodeURIComponent(orgId)}${tab ? `?tab=${tab}` : ''}`;

const COLUMNS = ['Store', 'Status', 'Plan', 'Subscription', 'Seats', 'Last active', 'Actions'];

export const GrantBadge: React.FC<{ grant: NonNullable<StoreAccountRow['accessGrant']> }> = ({ grant }) => {
  const left = daysLeft(grant.expiresAt);
  const tier = grant.fullAccess ? 'Full access' : planLabel(grant.tier);
  const label = [grantKindLabel(grant.kind), tier, grant.kind === 'trial' && left !== null ? `${left}d left` : null]
    .filter(Boolean)
    .join(' · ');
  const tone = grant.kind === 'trial' ? tokens.signal.caution : tokens.signal.info;
  return (
    <Tooltip
      title={`${grantKindLabel(grant.kind)} grant to ${tier}${left !== null ? `, ${left} days left` : ', no expiry'}`}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          mt: 0.5,
          px: 0.75,
          borderRadius: `${tokens.radius.chip}px`,
          border: `1px dashed ${tone.solid}`,
          color: tone.ink,
          fontSize: 11.5,
          fontWeight: 600,
          lineHeight: '20px',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
};

export const SeatsCell: React.FC<{ seats?: StoreAccountRow['seats'] }> = ({ seats }) => {
  if (!seats) return <Typography variant="caption">—</Typography>;
  const unlimited = seats.limit < 0;
  const tone = seatsTone(seats.used, seats.limit);
  const barColor =
    tone === 'problem' ? tokens.signal.problem.solid : tone === 'caution' ? tokens.signal.caution.solid : tokens.signal.info.solid;
  const pct = unlimited || seats.limit === 0 ? 0 : Math.min(100, (seats.used / seats.limit) * 100);
  return (
    <Box>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        <strong>{seats.used}</strong> / {unlimited ? '∞' : seats.limit}
      </Typography>
      {!unlimited && (
        <Box sx={{ mt: 0.5, width: 64, height: 4, borderRadius: 2, bgcolor: tokens.line.hairline }}>
          <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 2, bgcolor: barColor }} />
        </Box>
      )}
    </Box>
  );
};

export const LastActiveCell: React.FC<{ lastActive?: StoreAccountRow['lastActive'] }> = ({ lastActive }) => (
  <Box>
    <Typography
      variant="body2"
      sx={{ color: isStaleActivity(lastActive?.at) ? tokens.signal.caution.ink : undefined }}
    >
      {relativeTime(lastActive?.at)}
    </Typography>
    {lastActive?.userName && (
      <Typography variant="caption" color="text.secondary" noWrap>
        {lastActive.userName}
      </Typography>
    )}
  </Box>
);

const AdminStoreManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { startImpersonationWithOrg } = useImpersonation();
  const { reactivate } = useStoreStatusMutations();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statuses, setStatuses] = useState<string[]>(DEFAULT_STATUSES);
  const [plan, setPlan] = useState('any');
  const [subscription, setSubscription] = useState('any');
  const [sort, setSort] = useState('attention');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; store: StoreAccountRow } | null>(null);
  const [suspendFor, setSuspendFor] = useState<{ id: string; name: string } | null>(null);

  // Debounce the search box ~300ms before it drives a new server request.
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, statuses, plan, subscription, sort, rowsPerPage]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-store-accounts', page, rowsPerPage, debouncedQuery, statuses, plan, subscription, sort],
    queryFn: async (): Promise<StoreAccountPage> => {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        plan,
        subscription,
        sort,
      });
      if (statuses.length) params.set('status', statuses.join(','));
      if (debouncedQuery) params.set('search', debouncedQuery);
      const res = await makeApiCall(`/admin/organizations/store-accounts?${params.toString()}`, { headers });
      if (!res.ok) throw new Error('Failed to load stores');
      const body = await res.json();
      return body.data as StoreAccountPage;
    },
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pending = data?.pendingReview ?? [];
  const statusCounts = data?.statusCounts ?? {};

  const filtersActive = useMemo(
    () => !isDefaultStatuses(statuses) || plan !== 'any' || subscription !== 'any' || !!debouncedQuery,
    [statuses, plan, subscription, debouncedQuery]
  );

  const impersonate = (store: StoreAccountRow) => {
    startImpersonationWithOrg({
      id: store.id,
      name: store.name,
      type: 'store',
      address: [store.city, store.state].filter(Boolean).join(', '),
      status: store.status === 'active' ? 'active' : store.status.startsWith('pending') ? 'pending' : 'inactive',
      plan: planLabel(store.planTier),
    });
    navigate('/app/dashboard');
  };

  const toggleStatus = (value: string) =>
    setStatuses((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));

  const filterControlSx = (active: boolean) => ({
    height: 36,
    fontSize: 13,
    borderRadius: `${tokens.radius.small}px`,
    ...(active ? { bgcolor: tokens.accent.tint, '& fieldset': { borderColor: tokens.accent.border } } : {}),
  });

  const actions = (store: StoreAccountRow) => (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<Preview fontSize="small" />}
        onClick={() => impersonate(store)}
        aria-label={`Impersonate ${store.name}`}
      >
        Impersonate
      </Button>
      <Button
        size="small"
        variant="outlined"
        endIcon={<MoreVert fontSize="small" />}
        aria-haspopup="menu"
        aria-label={`More actions for ${store.name}`}
        onClick={(e) => setMenu({ anchor: e.currentTarget, store })}
      >
        More
      </Button>
    </Stack>
  );

  const subCell = (store: StoreAccountRow) => {
    const tone = tokens.signal[subscriptionTone(store.subscription.state)];
    const note = subscriptionNote(store.subscription);
    return (
      <Box>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone.solid, flexShrink: 0 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: tone.ink }}>
            {SUBSCRIPTION_LABELS[store.subscription.state]}
          </Typography>
        </Stack>
        {note && (
          <Typography
            variant="caption"
            sx={{ color: store.subscription.state === 'past_due' ? tokens.signal.problem.ink : 'text.secondary' }}
          >
            {note}
          </Typography>
        )}
      </Box>
    );
  };

  const planCell = (store: StoreAccountRow) => (
    <Box>
      <StatusChip label={planLabel(store.planTier)} tone={planTone(store.planTier)} />
      {store.accessGrant && (
        <Box>
          <GrantBadge grant={store.accessGrant} />
        </Box>
      )}
      {store.grantedFeatureCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          +{store.grantedFeatureCount} granted feature{store.grantedFeatureCount === 1 ? '' : 's'}
        </Typography>
      )}
    </Box>
  );

  const storeCell = (store: StoreAccountRow) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {store.name}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
        {[store.website ? store.website.replace(/^https?:\/\//i, '').replace(/\/$/, '') : 'No website',
          [store.city, store.state].filter(Boolean).join(', ')]
          .filter(Boolean)
          .join(' · ')}
      </Typography>
    </Box>
  );

  return (
    <PageContainer>
      <PageHeader
        title="Stores"
        description="Plans, seats and activity for every store organization. Click a store to manage it."
      />

      {pending.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            px: 2,
            borderRadius: `${tokens.radius.card}px`,
            border: `1px solid ${tokens.signal.caution.border}`,
            bgcolor: tokens.signal.caution.row,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: `${tokens.radius.small}px`,
                bgcolor: tokens.surface.card,
                border: `1px solid ${tokens.signal.caution.border}`,
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                color: tokens.signal.caution.ink,
              }}
            >
              {pending.length}
            </Box>
            <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: tokens.signal.caution.ink }}>
                {pending.length} {pending.length === 1 ? 'store is' : 'stores are'} waiting for review
              </Typography>
              {pending.slice(0, 5).map((p) => (
                <Typography key={p.id} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  <strong>{p.name}</strong> · {p.status === 'pending_verification' ? 'verification' : 'approval'}
                  {p.createdAt ? ` · signed up ${longDate(p.createdAt)}` : ''}
                </Typography>
              ))}
              {pending.length > 5 && (
                <Typography variant="caption" color="text.secondary">
                  and {pending.length - 5} more
                </Typography>
              )}
            </Box>
            <Button variant="outlined" size="small" onClick={() => navigate(bvRoutes.approvals())}>
              Open Approvals queue
            </Button>
          </Stack>
        </Box>
      )}

      <DataTableShell
        toolbar={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              placeholder="Search name, website, email or org ID"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ flex: '1 1 240px', maxWidth: 340 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ 'aria-label': 'Search stores' }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setStatusAnchor(e.currentTarget)}
              endIcon={<ArrowDropDown />}
              sx={{
                height: 36,
                ...(isDefaultStatuses(statuses)
                  ? {}
                  : { bgcolor: tokens.accent.tint, borderColor: tokens.accent.border }),
              }}
            >
              Status: {statusFilterLabel(statuses)}
            </Button>
            <Popover
              open={Boolean(statusAnchor)}
              anchorEl={statusAnchor}
              onClose={() => setStatusAnchor(null)}
              PaperProps={{ sx: { width: 230, p: 0.5 } }}
            >
              {ALL_STATUSES.map((s) => (
                <MenuItem key={s} dense onClick={() => toggleStatus(s)}>
                  <Checkbox size="small" checked={statuses.includes(s)} sx={{ mr: 0.5 }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {statusLabel(s)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {statusCounts[s] ?? 0}
                  </Typography>
                </MenuItem>
              ))}
              <Divider />
              <Stack direction="row" justifyContent="space-between" sx={{ px: 1, py: 0.5 }}>
                <Button size="small" onClick={() => setStatuses(DEFAULT_STATUSES)}>
                  Active + Pending
                </Button>
                <Button size="small" onClick={() => setStatuses([...ALL_STATUSES])}>
                  All
                </Button>
              </Stack>
            </Popover>
            <FormControl size="small">
              <Select
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                inputProps={{ 'aria-label': 'Plan' }}
                sx={filterControlSx(plan !== 'any')}
              >
                {PLAN_FILTERS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.value === 'any' ? 'Plan: Any' : o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <Select
                value={subscription}
                onChange={(e) => setSubscription(e.target.value)}
                inputProps={{ 'aria-label': 'Subscription' }}
                sx={filterControlSx(subscription !== 'any')}
              >
                {SUBSCRIPTION_FILTERS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.value === 'any' ? 'Subscription: Any' : o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <Select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                inputProps={{ 'aria-label': 'Sort' }}
                sx={filterControlSx(sort !== 'attention')}
              >
                {SORTS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    Sort: {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            {data && (
              <Typography variant="caption" color="text.secondary">
                {rows.length} of {total} {total === 1 ? 'store' : 'stores'}
              </Typography>
            )}
          </Stack>
        }
      >
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            <TableSkeleton rows={6} columns={5} />
          </Box>
        ) : isError ? (
          <Box sx={{ p: 2 }}>
            <InlineMessage severity="error">
              Could not load stores.{' '}
              <Button size="small" onClick={() => refetch()}>
                Retry
              </Button>
            </InlineMessage>
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState
            size="compact"
            title="No stores match these filters."
            description={filtersActive ? 'Try clearing the search or widening the status filter.' : undefined}
          />
        ) : isMobile ? (
          <Stack divider={<Divider />}>
            {rows.map((store) => (
              <Box
                key={store.id}
                sx={{ p: 2, cursor: 'pointer' }}
                onClick={() => navigate(storeDetailPath(store.id))}
              >
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                  {storeCell(store)}
                  <StatusChip label={statusLabel(store.status)} tone={storeStatusTone(store.status)} />
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  {planCell(store)}
                  {subCell(store)}
                  <SeatsCell seats={store.seats} />
                </Stack>
                <Box sx={{ mt: 1 }}>{actions(store)}</Box>
              </Box>
            ))}
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 1080 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: tokens.surface.sunken }}>
                  {COLUMNS.map((c) => (
                    <TableCell
                      key={c}
                      align={c === 'Actions' ? 'right' : 'left'}
                      sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}
                    >
                      {c}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((store) => (
                  <TableRow
                    key={store.id}
                    hover
                    onClick={() => navigate(storeDetailPath(store.id))}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{storeCell(store)}</TableCell>
                    <TableCell>
                      <StatusChip label={statusLabel(store.status)} tone={storeStatusTone(store.status)} />
                    </TableCell>
                    <TableCell>{planCell(store)}</TableCell>
                    <TableCell>{subCell(store)}</TableCell>
                    <TableCell>
                      <SeatsCell seats={store.seats} />
                    </TableCell>
                    <TableCell>
                      <LastActiveCell lastActive={store.lastActive} />
                    </TableCell>
                    <TableCell align="right">{actions(store)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!isLoading && !isError && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[25, 50, 100]}
          />
        )}
      </DataTableShell>

      <Menu
        anchorEl={menu?.anchor ?? null}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        PaperProps={{ sx: { width: 240 } }}
      >
        {menu && REVIEWABLE_STORE_STATUSES.includes(menu.store.status) && (
          <MenuItem
            onClick={() => {
              navigate(bvRoutes.approvals(storeCaseId(menu.store.id)));
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <FactCheck fontSize="small" />
            </ListItemIcon>
            Review in Approvals queue
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menu) navigate(storeDetailPath(menu.store.id, 'users'));
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <People fontSize="small" />
          </ListItemIcon>
          Manage users
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) navigate(storeDetailPath(menu.store.id, 'billing'));
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <CreditCard fontSize="small" />
          </ListItemIcon>
          Manage subscription & access
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) navigate(`/app/admin/store-sync-health?org=${encodeURIComponent(menu.store.id)}`);
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <Sync fontSize="small" />
          </ListItemIcon>
          Open ERP sync
        </MenuItem>
        <Divider />
        {menu?.store.status === 'suspended' ? (
          <MenuItem
            sx={{ color: tokens.signal.positive.ink }}
            onClick={() => {
              if (menu) reactivate.mutate({ orgId: menu.store.id, name: menu.store.name });
              setMenu(null);
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <PlayCircleOutline fontSize="small" />
            </ListItemIcon>
            Reactivate store
          </MenuItem>
        ) : (
          <MenuItem
            sx={{ color: tokens.signal.problem.ink }}
            disabled={menu ? REVIEWABLE_STORE_STATUSES.includes(menu.store.status) : false}
            onClick={() => {
              if (menu) setSuspendFor({ id: menu.store.id, name: menu.store.name });
              setMenu(null);
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <PauseCircleOutline fontSize="small" />
            </ListItemIcon>
            Suspend store…
          </MenuItem>
        )}
      </Menu>

      <SuspendStoreDialog store={suspendFor} onClose={() => setSuspendFor(null)} />
    </PageContainer>
  );
};

export default AdminStoreManagementPage;
